import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Walkthrough Needed", "Quote Sent", "Scheduled", "Completed", "Cancelled"];
const STATUS_COLOR = {
  New: "bg-blue-100 text-blue-700",
  Contacted: "bg-purple-100 text-purple-700",
  "Walkthrough Needed": "bg-amber-100 text-amber-700",
  "Quote Sent": "bg-violet-100 text-violet-700",
  Scheduled: "bg-emerald-100 text-emerald-700",
  Completed: "bg-slate-100 text-slate-700",
  Cancelled: "bg-rose-100 text-rose-700",
};
const SEGMENT_COLOR = {
  Residential: "bg-sky-100 text-sky-700",
  Commercial: "bg-violet-100 text-violet-700",
  Both: "bg-emerald-100 text-emerald-700",
  Other: "bg-slate-100 text-slate-700",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

export default function AdminRequests() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/estimates", { params: filter ? { status: filter } : {} });
      setItems(data);
    } catch (e) { toast.error("Failed to load"); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div data-testid="admin-requests-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-display font-bold text-[#0F172A]">Estimate Requests</h2>
          <p className="text-sm text-[#475569] mt-1">Click a row to view all customer-submitted details.</p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="admin-requests-filter"
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-[#475569]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#475569]" data-testid="admin-requests-empty">
            No estimate requests yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Services needed</th>
                  <th className="px-4 py-3">Preferred</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it) => {
                  const segment = it.residential_or_commercial || "Other";
                  return (
                    <tr key={it.id} className="hover:bg-slate-50" data-testid={`request-row-${it.id}`}>
                      <td className="px-4 py-3 font-medium text-[#0F172A] whitespace-nowrap">{it.full_name}</td>
                      <td className="px-4 py-3 text-[#475569] whitespace-nowrap">{it.phone}</td>
                      <td className="px-4 py-3 text-[#475569] break-all">{it.email}</td>
                      <td className="px-4 py-3 text-[#475569]" data-testid={`request-row-${it.id}-services`}>
                        <div className="flex flex-wrap gap-1 max-w-[260px]">
                          {(it.services_needed || []).map((s) => (
                            <span key={s} className="px-2 py-0.5 rounded-full bg-slate-100 text-[11px] font-medium text-[#0F172A] whitespace-nowrap">{s}</span>
                          ))}
                          {(it.services_needed || []).length === 0 && <span className="text-slate-400 text-xs">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#475569] whitespace-nowrap">{it.preferred_date || "—"}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${SEGMENT_COLOR[segment] || SEGMENT_COLOR.Other}`}>
                          {segment}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLOR[it.status] || "bg-slate-100"}`}>{it.status}</span>
                      </td>
                      <td className="px-4 py-3 text-[#475569] whitespace-nowrap text-xs">{fmtDate(it.created_at)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Link to={`/admin/requests/${it.id}`} className="text-[#1E3A8A] font-semibold hover:underline" data-testid={`request-row-${it.id}-open`}>
                          Open →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
